/*
 * „Ce urmează pe linii” (1 nov 2026, Dan): „când o linie e oprită, mecanicul să poată verifica ce mentenanțe pot să
 * apară peste câteva zile, să vadă starea lor pe anumite linii și să le selecteze ca să le facă”.
 *
 * Fila „📊 Ce urmează” din „Lucrările mele” (butonul din capul secțiunii și „🔧 Ce urmează” de pe cardul liniei).
 * Datele: `/api/mytasks/ce_urmeaza` (routes/maintenance/ce_urmeaza.py) — lucrările periodice depășite sau scadente în
 * zilele alese, cu aceeași socoteală ca planul zilei. Fiecare rând are „▶ Încep” și „✓ Am făcut” (aceleași funcții ca
 * planul zilei), iar cele bifate se pun în „Lucrările mele / Azi” prin `POST /api/operator_tasks`, pe numele omului.
 */
import { api, escHtml, state, toast, todayStr, truncate } from './core';

export interface LinieCeUrmeaza { id: string; produs: string; utilitati: boolean; principala: string | null;
    oprita_din: string | null }
export interface LucrareCeUrmeaza {
    interval_id: number; machine_id: string; action_ro: string | null; interval_hours: number;
    interval_text: string | null; category: string | null; critical: number; document_id: number | null;
    page_number: number | null; utilaj: string; linia: string; ultima_data: string | null; in_plan_azi: boolean;
    mod: 'ore' | 'calendar'; procent: number | null; ore_ramase: number | null; zile: number; depasit: boolean;
    scadenta: string | null; nivel: string; text: string;
}
interface RaspunsCeUrmeaza { linii: LinieCeUrmeaza[]; alese: string[]; zile: number; lucrari: LucrareCeUrmeaza[] }

const CHEIE_LINII = 'fm_ce_urmeaza_linii';
let _linii: string[] | null = null;
let _zile = 14;
let _actiuni: { incep: (b: Element) => unknown; amFacut: (b: HTMLElement) => unknown; reincarca: () => unknown } | null
    = null;

/** Funcțiile planului zilei („▶ Încep”, „✓ Am făcut”, reîncărcarea) — le dă tasks-calendar.ts, fără import circular. */
export function legaActiunile(a: NonNullable<typeof _actiuni>): void { _actiuni = a; }

/** Linia aleasă din afară (cardul liniei): doar ea. */
export function alegeLinia(linie: string): void {
    _linii = [linie];
    try { localStorage.setItem(CHEIE_LINII, JSON.stringify(_linii)); } catch { /* fără memorie locală */ }
}

function _liniiSalvate(): string[] | null {
    try {
        const v = JSON.parse(localStorage.getItem(CHEIE_LINII) || 'null');
        return Array.isArray(v) ? v.filter(x => typeof x === 'string') : null;
    } catch { return null; }
}

function _dataRo(s: string | null): string {
    if (!s) return '';
    const [a, l, z] = s.slice(0, 10).split('-');
    return `${z}.${l}.${a}`;
}

function _stare(t: LucrareCeUrmeaza): string {
    // textul și nivelul vin de pe server, din sursa unică (routes/maintenance/_stare.py) — la fel în toate paginile
    const culoare = t.nivel === 'depasit' ? 'var(--red, #ef4444)' : t.nivel === 'aproape' ? '#f59e0b' : 'var(--green, #22c55e)';
    const productie = t.mod === 'ore' && !t.depasit ? ` (≈ ${t.zile} ${t.zile === 1 ? 'zi' : 'zile'} de producție)` : '';
    return `<span class="cu-stare" style="display:inline-block;padding:3px 9px;border-radius:10px;font-size:.78rem;` +
        `font-weight:700;border:1px solid ${culoare};color:${culoare}">${escHtml(t.text + productie)}</span>`;
}

function _rand(t: LucrareCeUrmeaza): string {
    const uid = `cu-${t.machine_id}-${t.interval_id}`;
    const date = `data-uid="${escHtml(uid)}" data-mid="${escHtml(t.machine_id)}" data-ivid="${t.interval_id}" ` +
        `data-action="${escHtml(t.action_ro || '')}" data-ih="${t.interval_hours || 0}" data-taskid="" ` +
        `data-critical="${t.critical ? 1 : 0}" data-cat="${escHtml(t.category || '')}"`;
    const manual = t.document_id
        ? `<a href="/api/documents/${t.document_id}/view${t.page_number ? '?page=' + t.page_number : ''}" target="_blank" ` +
          `class="btn btn-sm" style="font-size:.8rem;padding:6px 10px;color:#00b4ff;border:1px solid rgba(0,180,255,.35);` +
          `text-decoration:none;border-radius:8px">📖 Manual${t.page_number ? ' p.' + t.page_number : ''}</a>` : '';
    return `<div class="cu-rand" data-ivid="${t.interval_id}" style="display:flex;gap:10px;align-items:flex-start;` +
        `padding:10px 0;border-top:1px solid var(--border);flex-wrap:wrap">` +
        `<label style="display:flex;align-items:flex-start;gap:10px;flex:1;min-width:220px;cursor:pointer">` +
        `<input type="checkbox" class="cu-bifa" data-ivid="${t.interval_id}" ${t.in_plan_azi ? 'disabled' : ''} ` +
        `style="width:24px;height:24px;flex:none;margin-top:2px">` +
        `<span style="display:flex;flex-direction:column;gap:4px">` +
        `<span style="font-size:.92rem;color:var(--text)" title="${escHtml(t.action_ro || '')}">` +
        `${escHtml(truncate(t.action_ro || '', 130))}</span>` +
        `<span style="font-size:.76rem;color:var(--text-muted)">${escHtml(t.interval_text || '')}` +
        `${t.category ? ' · ' + escHtml(t.category) : ''}${t.critical ? ' · <b style="color:#ef4444">CRITIC</b>' : ''}` +
        `${t.ultima_data ? ' · ultima: ' + escHtml(_dataRo(t.ultima_data)) : ''}</span>` +
        `<span>${_stare(t)}${t.in_plan_azi ? ' <span style="font-size:.76rem;color:var(--accent)">✓ deja în planul de azi</span>' : ''}</span>` +
        `</span></label>` +
        `<div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center">${manual}` +
        `<button type="button" class="btn btn-green btn-sm cu-incep" ${date} style="font-size:.85rem;padding:9px 14px;` +
        `min-height:44px">▶ Încep</button>` +
        `<button type="button" class="btn cu-am-facut" ${date} style="font-size:.85rem;font-weight:700;padding:9px 14px;` +
        `min-height:44px;background:#00e676;color:#000;border:none;border-radius:10px">✓ Am făcut</button></div></div>`;
}

export async function randeazaCeUrmeaza(gazda: HTMLElement): Promise<void> {
    gazda.innerHTML = '<div class="alarm-empty"><span class="icon">⏳</span>Se încarcă ce urmează pe linii…</div>';
    if (_linii === null) _linii = _liniiSalvate();
    let d: RaspunsCeUrmeaza;
    try {
        // prima dată, fără linii alese: aflăm liniile, apoi alegem implicit liniile oprite
        if (!_linii || !_linii.length) {
            const lista: RaspunsCeUrmeaza = await api('/api/mytasks/ce_urmeaza');
            const oprite = lista.linii.filter(l => l.oprita_din && !l.utilitati).map(l => l.id);
            _linii = oprite.length ? oprite : lista.linii.filter(l => !l.utilitati).slice(0, 1).map(l => l.id);
        }
        d = await api(`/api/mytasks/ce_urmeaza?linii=${encodeURIComponent(_linii.join(','))}&zile=${_zile}`);
    } catch (e) {
        gazda.innerHTML = `<div class="alarm-empty"><span class="icon">⚠️</span>Nu s-a putut încărca: ${escHtml((e as Error).message)}</div>`;
        return;
    }
    _linii = d.alese;
    const pe = new Map(d.linii.map(l => [l.id, l]));
    const chipLinie = (l: LinieCeUrmeaza) => {
        const ales = _linii!.includes(l.id);
        return `<button type="button" class="btn btn-sm cu-linie${ales ? ' btn-active' : ''}" data-linie="${escHtml(l.id)}" ` +
            `aria-pressed="${ales}" style="min-height:44px;padding:8px 12px;border-radius:10px;` +
            `${ales ? 'background:var(--accent);color:#fff;border-color:var(--accent)' : ''}">` +
            `${l.oprita_din ? '⏸ ' : ''}${escHtml(l.id)}` +
            `${l.oprita_din ? `<span style="font-size:.72rem;opacity:.85"> · oprită din ${escHtml(_dataRo(l.oprita_din))}</span>` : ''}` +
            `</button>`;
    };
    let html = `<div class="cu-cap" style="display:flex;flex-direction:column;gap:10px;margin-bottom:14px">` +
        `<h3 style="margin:0;font-size:1.05rem">📊 Ce urmează pe linii</h3>` +
        `<div style="font-size:.85rem;color:var(--text-muted)">Lucrările periodice depășite sau care ajung la scadență în ` +
        `zilele alese — cât linia stă, le poți face acum. Pe cele cu ore: cât s-a consumat din interval și ≈ câte zile de ` +
        `producție mai sunt. Lucrările zilnice nu sunt aici: le găsești în planul fiecărei zile.</div>` +
        `<div class="cu-linii" style="display:flex;gap:8px;flex-wrap:wrap">${d.linii.map(chipLinie).join('')}</div>` +
        `<div class="cu-zile" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">` +
        `<span style="font-size:.85rem;color:var(--text-muted)">În următoarele:</span>` +
        [7, 14, 30].map(z => `<button type="button" class="btn btn-sm cu-zi${z === d.zile ? ' btn-active' : ''}" data-zile="${z}" ` +
            `style="min-height:40px;${z === d.zile ? 'background:var(--accent);color:#fff;border-color:var(--accent)' : ''}">${z} zile</button>`).join('') +
        `</div></div>`;
    if (!_linii.length) {
        html += '<div class="alarm-empty"><span class="icon">👆</span>Alege una sau mai multe linii.</div>';
    } else if (!d.lucrari.length) {
        html += `<div class="alarm-empty"><span class="icon">✅</span>Nicio lucrare depășită sau scadentă în următoarele ${d.zile} zile pe ${escHtml(_linii.join(', '))}.</div>`;
    } else {
        for (const lid of _linii) {
            const aleLiniei = d.lucrari.filter(t => (t.linia || '').split(',').map(x => x.trim()).includes(lid));
            if (!aleLiniei.length) continue;
            const l = pe.get(lid);
            html += `<section class="cu-sectiune" data-linie="${escHtml(lid)}" style="margin-bottom:16px;padding:12px;border:1px solid var(--border);border-radius:12px">` +
                `<h4 style="margin:0 0 6px;font-size:.98rem">Linia ${escHtml(lid)}${l?.produs ? ' — ' + escHtml(l.produs) : ''}` +
                `${l?.oprita_din ? ` <span style="font-size:.78rem;color:#f59e0b">⏸ oprită din ${escHtml(_dataRo(l.oprita_din))}</span>` : ''}` +
                ` <span style="font-size:.78rem;color:var(--text-muted)">· ${aleLiniei.length} lucrări</span></h4>`;
            const peUtilaj = new Map<string, LucrareCeUrmeaza[]>();
            aleLiniei.forEach(t => { const k = t.utilaj || t.machine_id; peUtilaj.set(k, [...(peUtilaj.get(k) || []), t]); });
            peUtilaj.forEach((lista, ut) => {
                html += `<div class="cu-utilaj" style="margin-top:8px"><div style="font-weight:700;font-size:.9rem">${escHtml(ut)}` +
                    ` <span style="font-weight:400;font-size:.78rem;color:var(--text-muted)">${lista.length}</span></div>` +
                    lista.map(_rand).join('') + '</div>';
            });
            html += '</section>';
        }
        html += `<div class="cu-bara" style="position:sticky;bottom:0;padding:10px 0;background:var(--bg, var(--card-bg));` +
            `display:flex;gap:10px;align-items:center;flex-wrap:wrap;border-top:1px solid var(--border)">` +
            `<button type="button" class="btn btn-green" id="cu-pune-azi" disabled style="min-height:48px;padding:10px 18px;` +
            `font-weight:700">Pune cele bifate în lucrările mele de azi</button>` +
            `<span id="cu-bifate" style="font-size:.85rem;color:var(--text-muted)">Bifează lucrările pe care le faci cât stă linia.</span></div>`;
    }
    gazda.innerHTML = html;
    _leaga(gazda, d);
}

function _leaga(gazda: HTMLElement, d: RaspunsCeUrmeaza): void {
    gazda.querySelectorAll<HTMLElement>('.cu-linie').forEach(b => b.addEventListener('click', () => {
        const id = b.dataset.linie || '';
        _linii = _linii!.includes(id) ? _linii!.filter(x => x !== id) : [..._linii!, id];
        try { localStorage.setItem(CHEIE_LINII, JSON.stringify(_linii)); } catch { /* fără memorie locală */ }
        void randeazaCeUrmeaza(gazda);
    }));
    gazda.querySelectorAll<HTMLElement>('.cu-zi').forEach(b => b.addEventListener('click', () => {
        _zile = parseInt(b.dataset.zile || '14', 10) || 14;
        void randeazaCeUrmeaza(gazda);
    }));
    gazda.querySelectorAll<HTMLElement>('.cu-incep').forEach(b => b.addEventListener('click', () => _actiuni?.incep(b)));
    gazda.querySelectorAll<HTMLElement>('.cu-am-facut').forEach(b => b.addEventListener('click', () => _actiuni?.amFacut(b)));
    const btn = gazda.querySelector('#cu-pune-azi') as HTMLButtonElement | null;
    const eticheta = gazda.querySelector('#cu-bifate') as HTMLElement | null;
    const bifate = () => [...gazda.querySelectorAll<HTMLInputElement>('.cu-bifa:checked')];
    gazda.querySelectorAll('.cu-bifa').forEach(c => c.addEventListener('change', () => {
        const n = bifate().length;
        if (btn) { btn.disabled = n === 0; btn.textContent = n ? `Pune cele ${n} bifate în lucrările mele de azi` : 'Pune cele bifate în lucrările mele de azi'; }
        if (eticheta) eticheta.textContent = n ? '' : 'Bifează lucrările pe care le faci cât stă linia.';
    }));
    btn?.addEventListener('click', async () => {
        const ids = bifate().map(c => parseInt(c.dataset.ivid || '0', 10));
        const lucrari = d.lucrari.filter(t => ids.includes(t.interval_id));
        if (!lucrari.length) return;
        btn.disabled = true;
        btn.textContent = '⏳ Se pun în plan…';
        const eu = state.currentUser?.username || '';
        let puse = 0;
        const erori: string[] = [];
        for (const t of lucrari) {
            try {
                await api('/api/operator_tasks', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        machine_id: t.machine_id, interval_id: t.interval_id, due_date: todayStr(), status: 'pending',
                        assigned_to: eu, line_id: (t.linia || '').split(',')[0].trim(),
                        // ocurența din calendar pe care o înlocuiește — să nu apară de două ori în plan
                        replaces_calendar_date: t.mod === 'calendar' ? t.scadenta : null,
                    }),
                });
                puse++;
            } catch (e) {
                erori.push(`${t.utilaj}: ${(e as Error).message}`);
            }
        }
        if (puse) toast(`Am pus ${puse} ${puse === 1 ? 'lucrare' : 'lucrări'} în «Lucrările mele / Azi»`, 'success');
        if (erori.length) toast(`Nu s-au putut pune ${erori.length}: ${erori[0]}`, 'error');
        _actiuni?.reincarca();
    });
}
